import { useCallback } from 'react'

import { useFormContext } from 'react-hook-form'

import { TransferFormValues } from '~/components/forms/transfer/transferSchema'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { IAddress } from '~/types'
import { Address } from '~/utils/address'

export const INTERNAL_TRANSFER_OUT_MEMO = 'Internal transfer out'
export const SELF_TRANSFER_MEMO = 'Self transfer'

export const useMemoPrefill = ({
  isMultiRecipient,
}: {
  isMultiRecipient: boolean
}) => {
  const { getValues, setValue, formState } =
    useFormContext<TransferFormValues>()
  const { smartAccounts } = useSmartAccounts()
  const isMemoDirty = formState.dirtyFields.memo

  return useCallback(
    ({
      senderAddress,
      recipientAddress,
    }: {
      senderAddress?: IAddress
      recipientAddress?: IAddress
    }) => {
      if (isMultiRecipient) return
      if (isMemoDirty) return

      const sender = senderAddress ?? getValues('sender')
      const recipient =
        recipientAddress ?? getValues('recipients.0.receiver.address')

      const current = getValues('memo') ?? ''
      const isPrefilledMemo =
        current === INTERNAL_TRANSFER_OUT_MEMO || current === SELF_TRANSFER_MEMO
      if (current !== '' && !isPrefilledMemo) return

      const isInternal = smartAccounts.some((a) =>
        Address.equal(a.address, recipient),
      )
      const isSelf = Address.equal(sender, recipient)

      const desired = isSelf
        ? SELF_TRANSFER_MEMO
        : isInternal
          ? INTERNAL_TRANSFER_OUT_MEMO
          : ''

      if (current === desired) return
      setValue('memo', desired, { shouldDirty: false })
    },
    [isMultiRecipient, isMemoDirty, smartAccounts, getValues, setValue],
  )
}
